import { useTicketHistory } from '../hooks/useTickets';
import { useDateFormat } from '../hooks/useDateFormat';
import type { TicketHistoryEntry } from '../types/tickets';
import { Card } from './ui/card';
import { getActivityIcon } from '@/lib/activityIcons';
import { cn } from '@/lib/utils';
import { Zap } from 'lucide-react';

interface TicketHistoryProps {
  ticketId: string;
}

export function TicketHistory({ ticketId }: TicketHistoryProps) {
  const { data: history = [], isLoading, error } = useTicketHistory(ticketId);
  const { formatDateTime } = useDateFormat();

  if (isLoading) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Activity History</h3>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
              <div className="flex-1">
                <div className="h-4 bg-muted rounded animate-pulse mb-2" />
                <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Activity History</h3>
        <div className="text-center py-8">
          <p className="text-muted-foreground">Failed to load activity history</p>
        </div>
      </Card>
    );
  }




  // Helper function to check if a field is a date field
  const isDateField = (fieldName?: string | null): boolean => {
    if (!fieldName) return false;
    return ['Due Date', 'DueAt', 'Created Date', 'CreatedAt', 'Updated Date', 'UpdatedAt', 'Resolved Date', 'ResolvedAt', 'Closed Date', 'ClosedAt'].includes(fieldName);
  };

  // Helper function to format a date value for display
  const formatDateValue = (dateValue: string | null): string => {
    if (!dateValue) return 'Not set';
    try {
      return formatDateTime(dateValue);
    } catch {
      return dateValue; // Fallback to original value if parsing fails
    }
  };

  const formatHistoryEntry = (entry: TicketHistoryEntry) => {
    if (entry.description) {
      // Check if this is a date field with template placeholders (new format)
      if (entry.field && isDateField(entry.field) && (entry.description.includes('{oldValue}') || entry.description.includes('{newValue}'))) {
        let formattedDescription = entry.description;
        
        // Replace {oldValue} with formatted date
        if (entry.oldValue && formattedDescription.includes('{oldValue}')) {
          const formattedOldValue = formatDateValue(entry.oldValue);
          formattedDescription = formattedDescription.replace('{oldValue}', formattedOldValue);
        }
        
        // Replace {newValue} with formatted date
        if (entry.newValue && formattedDescription.includes('{newValue}')) {
          const formattedNewValue = formatDateValue(entry.newValue);
          formattedDescription = formattedDescription.replace('{newValue}', formattedNewValue);
        }
        
        return formattedDescription;
      }
      
      // Handle legacy format - detect embedded UTC date strings and convert them
      if (entry.field && isDateField(entry.field)) {
        // Regex to detect UTC datetime patterns like "2025-08-26 14:59"
        const datePattern = /\b(\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})?)\b/g;
        
        return entry.description.replace(datePattern, (match) => {
          try {
            // Try to parse and format the detected date
            return formatDateTime(match);
          } catch {
            return match; // Return original if parsing fails
          }
        });
      }
      
      return entry.description;
    }

    // Fallback formatting if no description
    if (entry.field && entry.oldValue && entry.newValue) {
      if (isDateField(entry.field)) {
        return `${entry.field} changed from "${formatDateValue(entry.oldValue)}" to "${formatDateValue(entry.newValue)}"`;
      }
      return `${entry.field} changed from "${entry.oldValue}" to "${entry.newValue}"`;
    }

    if (entry.field && entry.newValue && !entry.oldValue) {
      if (isDateField(entry.field)) {
        return `${entry.field} set to "${formatDateValue(entry.newValue)}"`;
      }
      return `${entry.field} set to "${entry.newValue}"`;
    }

    return entry.action;
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Activity History
      </h3>

      {history.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No activity history available</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto border rounded-md p-4 bg-muted/20 scroll-smooth">
          <div className="space-y-4">
            {history.map((entry, index) => {
              const { icon: Icon, color, bgColor } = getActivityIcon(entry.action);
              return (
            <div key={entry.id} className="flex gap-3">
              {/* Timeline connector */}
              <div className="flex flex-col items-center">
                <div className={cn('p-1.5 rounded-md shrink-0', bgColor)}>
                  <Icon className={cn('h-3.5 w-3.5', color)} />
                </div>
                {index < history.length - 1 && (
                  <div className="w-0.5 flex-1 bg-border mt-1" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {formatHistoryEntry(entry)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {entry.changedBy?.fullName || (
                          entry.automationRuleName ? (
                            <span className="inline-flex items-center gap-1">
                              <Zap className="h-3 w-3" />
                              Rule &quot;{entry.automationRuleName}&quot;
                            </span>
                          ) : 'System'
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(entry.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Show field changes if available */}
                {entry.field && entry.oldValue && entry.newValue && !entry.description && (
                  <div className="mt-2 text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                    <span className="font-medium">{entry.field}:</span>{' '}
                    <span className="line-through">
                      {isDateField(entry.field) ? formatDateValue(entry.oldValue) : entry.oldValue}
                    </span>{' '}
                    → <span className="font-medium">
                      {isDateField(entry.field) ? formatDateValue(entry.newValue) : entry.newValue}
                    </span>
                  </div>
                )}
              </div>
            </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}